// Mirror of executive-dashboard permissions for the currently logged-in user.
// One browser → one logged-in user, so we keep a FLAT object in localStorage
// (no per-userId dict). App.tsx writes here on whoami; route gates and
// non-React call sites read from here without needing a userId argument.

import { useSelector } from 'react-redux'
import { selectMe } from 'app/slice/user.slice'
import { isDevSuperAdmin } from 'utils/app.utils'
import type { UserExecutivePermissions } from 'api/executive/executive.types'

const STORAGE_KEY = 'EXECUTIVE_PERMISSIONS'

export const DEFAULT_EXECUTIVE_PERMISSIONS: UserExecutivePermissions = {
  executiveDashboardTabs: {
    performance: false,
    account: false,
    sales: false,
    consumerInsight: false,
  },
  executiveDashboardScope: 'own-branch',
  canManageDashboardTargets: false,
}

// Dev-only escape hatch mirroring utils/app.utils.ts#checkPackage etc. —
// grants every tab + target-management in development when DEV_SUPER_ADMIN=1.
const DEV_SUPER_ADMIN_PERMISSIONS: UserExecutivePermissions = {
  executiveDashboardTabs: {
    performance: true,
    account: true,
    sales: true,
    consumerInsight: true,
  },
  executiveDashboardScope: 'all-branches',
  canManageDashboardTargets: true,
}

/**
 * Merge a partial executive-permissions blob (from backend or localStorage)
 * with safe defaults. Tolerates string payloads (when the MySQL JSON column
 * round-trips un-parsed) and missing inner sections.
 */
function mergeWithDefaults(input: unknown): UserExecutivePermissions {
  let raw: any = input
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw) } catch { raw = null }
  }
  if (!raw || typeof raw !== 'object') return DEFAULT_EXECUTIVE_PERMISSIONS
  return {
    executiveDashboardTabs: {
      ...DEFAULT_EXECUTIVE_PERMISSIONS.executiveDashboardTabs,
      ...(raw.executiveDashboardTabs ?? {}),
    },
    executiveDashboardScope:
      raw.executiveDashboardScope ?? DEFAULT_EXECUTIVE_PERMISSIONS.executiveDashboardScope,
    canManageDashboardTargets:
      raw.canManageDashboardTargets ?? DEFAULT_EXECUTIVE_PERMISSIONS.canManageDashboardTargets,
  }
}

/** Reads the raw stored permission, ignoring the dev-bypass. */
export function loadStoredExecutivePermissions(): UserExecutivePermissions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_EXECUTIVE_PERMISSIONS
    return mergeWithDefaults(JSON.parse(raw))
  } catch {
    return DEFAULT_EXECUTIVE_PERMISSIONS
  }
}

/** Reads the effective permission (DEV_SUPER_ADMIN bypass → storage → default). */
export function loadExecutivePermissions(): UserExecutivePermissions {
  if (process.env.NODE_ENV === 'development' && isDevSuperAdmin()) {
    return DEV_SUPER_ADMIN_PERMISSIONS
  }
  return loadStoredExecutivePermissions()
}

export function saveExecutivePermissions(value: UserExecutivePermissions): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // ignore quota errors
  }
}

export function clearExecutivePermissions(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function hasAnyExecutiveTab(): boolean {
  const t = loadExecutivePermissions().executiveDashboardTabs
  return !!(t.performance || t.account || t.sales || t.consumerInsight)
}

export function canManageTargets(): boolean {
  return loadExecutivePermissions().canManageDashboardTargets
}

/**
 * React hook for components that need executive permissions. Resolution order:
 *   1. DEV_SUPER_ADMIN bypass (dev only)
 *   2. Current logged-in user's redux `me.executivePermissions` — freshest source,
 *      reflects backend immediately after whoami / personnel save
 *   3. localStorage (mirrored by App.tsx after whoami)
 *   4. Hard default (all-false)
 * Re-renders automatically when redux `me` changes.
 */
export function useExecutivePermissions(): UserExecutivePermissions {
  const me = useSelector(selectMe) as any
  if (process.env.NODE_ENV === 'development' && isDevSuperAdmin()) {
    return DEV_SUPER_ADMIN_PERMISSIONS
  }
  const fromMe = me?.executivePermissions
  if (fromMe) return mergeWithDefaults(fromMe)
  return loadStoredExecutivePermissions()
}
